import React from 'react';
import { observer } from 'mobx-react-lite';
import { DialogDimension, DialogContentType, IAppManifest } from '@metaos/hub-sdk';
import { Modal, Stack, ImageIcon, IconButton, mergeStyles } from '@fluentui/react';
import loc from 'owa-localize';
import { confirmDialogCloseText } from 'owa-locstrings/lib/strings/confirmdialogclosetext.locstring.json';
import type { PromiseHandlers } from 'owa-app-host-services';
import { TaskModuleState } from '../../utils/createTaskModule';
import convertDialogSizeToCss from '../../utils/convertDialogSizeToCssUnit';
import TaskModuleContentUrl from './TaskModuleContentUrl';
import styles from './TaskModule.scss';

export interface ITaskModule extends Omit<TaskModuleState, 'usePromiseState'> {
    shouldShowHeader: boolean;
    appDefinition: IAppManifest;
    promiseHandlers: PromiseHandlers;
}

function TaskModuleContentRenderer<T extends DialogContentType>(props: ITaskModule) {
    const { dialogInfo } = props.store();
    const taskModuleType: string | undefined = dialogInfo.get('type') || '';
    const dialogTypeReverseMap = {
        card: DialogContentType.Card,
        url: DialogContentType.Url,
    };

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7053 (30,13): Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ card: DialogContentType; url: DialogContentType; }'.
    // @ts-expect-error
    switch (dialogTypeReverseMap[taskModuleType as string] as T) {
        case DialogContentType.Url:
            const contentUrl = dialogInfo.get('url');
            if (!contentUrl) {
                return null;
            }
            return <TaskModuleContentUrl {...{ contentUrl, ...props }} />;
        case DialogContentType.Card:
            // TODO: Plug <TaskModuleContentCard/>
            return null;
        default:
            // TODO: Monitor it.
            return null;
    }
}

function TaskModuleHeaderRenderer(props: ITaskModule) {
    const { appDefinition, store, promiseHandlers, shouldShowHeader } = props;
    const { dialogInfo } = store();
    if (shouldShowHeader) {
        return (
            <Stack.Item className={styles.dialogHeader}>
                <ImageIcon
                    className={styles.dialogIcon}
                    imageProps={{ src: appDefinition.smallImageUrl }}
                />
                <Stack>
                    <Stack.Item className={styles.labelTitle}>{dialogInfo.get('title')}</Stack.Item>
                    <Stack.Item className={styles.labelAppName}>{appDefinition.name}</Stack.Item>
                </Stack>
                <IconButton
                    title={loc(confirmDialogCloseText)}
                    ariaLabel={loc(confirmDialogCloseText)}
                    onClick={promiseHandlers.onDismiss}
                    iconProps={{ iconName: 'Cancel' }}
                    styles={{ root: { marginLeft: 'auto' } }}
                />
            </Stack.Item>
        );
    }

    return (
        <Stack.Item>
            <IconButton
                title={loc(confirmDialogCloseText)}
                ariaLabel={loc(confirmDialogCloseText)}
                onClick={promiseHandlers.onDismiss}
                iconProps={{ iconName: 'Cancel' }}
                styles={{ root: { position: 'absolute', right: '32px', top: '32px' } }}
            />
        </Stack.Item>
    );
}

export default observer(function TaskModule(props: ITaskModule) {
    const { store, promiseHandlers, shouldShowHeader } = props;
    const { isOpen, dialogInfo } = store();

    const Content = React.useCallback(() => <TaskModuleContentRenderer {...props} />, [isOpen]);
    const Header = React.useCallback(() => <TaskModuleHeaderRenderer {...props} />, [
        shouldShowHeader,
    ]);

    if (!isOpen || !dialogInfo) {
        return null;
    }

    const modalContainerStyles = mergeStyles({
        ...convertDialogSizeToCss({
            width: (dialogInfo.get('width') || '66px') as DialogDimension | number,
            height: (dialogInfo.get('height') || '66px') as DialogDimension | number,
        }),

        padding: shouldShowHeader ? '32px' : '0px',
        minWidth: '20%',
        minHeight: '20%',
        maxWidth: '80%',
        maxHeight: '80%',
    });

    return (
        <Modal
            containerClassName={modalContainerStyles}
            scrollableContentClassName={styles.modal}
            onDismiss={promiseHandlers.onDismiss}
            isOpen={isOpen}>
            <Stack className={styles.dialogContainer}>
                <Header />
                <Stack.Item className={styles.dialogBody}>
                    <Content />
                </Stack.Item>
            </Stack>
        </Modal>
    );
});
